/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from '@fluidframework/common-utils';
import { copyPropertyIfDefined, fail } from './Common';
import { isParentedForestNode } from './Forest';
import { Side } from './persisted-types';
/**
 * A view of a distributed tree.
 * @public
 */
export class TreeView {
    constructor(root, forest) {
        this.root = root;
        this.forest = forest;
        this.rootNode = this.getViewNode(root);
    }
    /** @returns the number of nodes in this view */
    get size() {
        return this.forest.size;
    }
    /** @returns true iff a node with the given id exists in this view */
    hasNode(id) {
        return this.forest.has(id);
    }
    /**
     * @returns the index just after place (which specifies a location between items).
     * Performance note: this is O(siblings in trait).
     */
    findIndexWithinTrait(place) {
        if (place.sibling === undefined) {
            return this.getIndexOfSide(place.side, place.trait);
        }
        return getIndex(place.side, this.getIndexInTrait(place.sibling));
    }
    /** @returns the node associated with the given id in this view. Fails if the node does not exist in this view. */
    getViewNode(id) {
        var _a;
        return (_a = this.tryGetViewNode(id)) !== null && _a !== void 0 ? _a : fail('NodeId not found');
    }
    /** @returns the node associated with the given id in this view, or undefined if the node does not exist in this view */
    tryGetViewNode(id) {
        const forestNode = this.forest.tryGet(id);
        if (forestNode !== undefined && isParentedForestNode(forestNode)) {
            const viewNode = {
                definition: forestNode.definition,
                identifier: forestNode.identifier,
                traits: forestNode.traits,
                parentage: {
                    label: forestNode.traitParent,
                    parent: forestNode.parentId,
                },
            };
            copyPropertyIfDefined(forestNode, viewNode, 'payload');
            return viewNode;
        }
        return forestNode;
    }
    /**
     * @returns the label of the trait under which a node with the given id resides. Fails if the node does not exist in this view or if
     * it is the root node.
     */
    getTraitLabel(id) {
        return this.forest.getParent(id).traitParent;
    }
    /**
     * @returns the label of the trait under which a node with the given id resides, or undefined if the node is not present in this
     * view or if it is the root node
     */
    tryGetTraitLabel(id) {
        var _a;
        return (_a = this.forest.tryGetParent(id)) === null || _a === void 0 ? void 0 : _a.traitParent;
    }
    /**
     * @returns the parent of the node with the given id. Fails if the node does not exist in this view or if it is the root node.
     */
    getParentViewNode(id) {
        const parentInfo = this.forest.getParent(id);
        return this.getViewNode(parentInfo.parentId);
    }
    /**
     * @returns the parent of the node with the given id. Returns undefined if the node does not exist in this view or if it is the root
     * node.
     */
    tryGetParentViewNode(id) {
        const parentInfo = this.forest.tryGetParent(id);
        if (parentInfo === undefined) {
            return undefined;
        }
        return this.getViewNode(parentInfo.parentId);
    }
    /**
     * @returns the trait location of the node with the given id. Fails if the node does not exist in this view or of it is the root
     * node
     */
    getTraitLocation(id) {
        const parentData = this.forest.getParent(id);
        return {
            parent: parentData.parentId,
            label: parentData.traitParent,
        };
    }
    /**
     * @returns the trait location of the node with the given id, or undefined if the node does not exist in this view or if it is the root
     * node
     */
    tryGetTraitLocation(id) {
        const parentData = this.forest.tryGetParent(id);
        if (parentData === undefined) {
            return undefined;
        }
        return {
            parent: parentData.parentId,
            label: parentData.traitParent,
        };
    }
    /**
     * @returns the index within the trait under which the node with the given id resides. The node must exist in this view and must have a
     * parent.
     * Performance note: this is O(siblings in trait).
     */
    getIndexInTrait(id) {
        const index = this.tryGetIndexInTrait(id);
        return index !== null && index !== void 0 ? index : fail('ID does not exist in the forest.');
    }
    /**
     * @returns the index within the trait under which the node with the given id resides, or undefined if the node does not exist in this
     * view or does not have a parent.
     * Performance note: this is O(siblings in trait).
     */
    tryGetIndexInTrait(id) {
        var _a, _b, _c;
        const cachedIndex = (_a = this.traitIndicesCache) === null || _a === void 0 ? void 0 : _a.get(id);
        if (cachedIndex !== undefined) {
            return cachedIndex;
        }
        const parentData = this.forest.tryGetParent(id);
        if (parentData === undefined) {
            return undefined;
        }
        const parent = this.forest.tryGet(parentData.parentId);
        if (parent === undefined) {
            return undefined;
        }
        const trait = (_b = parent.traits.get(parentData.traitParent)) !== null && _b !== void 0 ? _b : fail('inconsistent forest: trait parent not found');
        let foundIndex;
        if (trait.length === 0) {
            return foundIndex;
        }
        (_c = this.traitIndicesCache) !== null && _c !== void 0 ? _c : (this.traitIndicesCache = new Map());
        for (let i = 0; i < trait.length; i++) {
            const nodeInTrait = trait[i];
            const index = i;
            this.traitIndicesCache.set(nodeInTrait, index);
            if (nodeInTrait === id) {
                foundIndex = index;
            }
        }
        return foundIndex;
    }
    /**
     * @returns the trait at the given location. If no such trait exists, returns an empty trait.
     */
    getTrait(traitLocation) {
        var _a;
        return (_a = this.getViewNode(traitLocation.parent).traits.get(traitLocation.label)) !== null && _a !== void 0 ? _a : [];
    }
    /** Asserts that the view's internal state is consistent. Useful for testing/validation. */
    assertConsistent() {
        this.forest.assertConsistent();
    }
    [Symbol.iterator]() {
        return this.iterateNodeDescendants(this.rootNode);
    }
    /**
     * @returns true iff the given view's forest of nodes is equivalent to this view's forest of nodes
     * @param strict - if true, the views' forests must be the same object, otherwise they must merely be equivalent
     */
    hasEqualForest(view, strict = false) {
        if (this.root === view.root) {
            // TODO:#49100:Perf: make this faster and/or remove use by PrefetchingCheckout.
            return strict ? this.forest === view.forest : this.forest.equals(view.forest);
        }
        return false;
    }
    *iterateNodeDescendants(node) {
        yield node;
        for (const label of [...node.traits.keys()].sort()) {
            const trait = node.traits.get(label);
            for (const childId of trait !== null && trait !== void 0 ? trait : fail('Expected trait with label')) {
                const child = this.getViewNode(childId);
                yield* this.iterateNodeDescendants(child);
            }
        }
    }
    getIndexOfSide(side, traitLocation) {
        return side === Side.After ? 0 : this.getTrait(traitLocation).length;
    }
    /**
     * Calculate the difference between two `TreeView`s
     * @param view - the other view to compare to this one
     * @returns A {@link Delta} which nodes must be changed, added, and removed to get from `this` to `view`.
     * The views must share a root.
     */
    delta(view) {
        assert(this.root === view.root, 0x63d /* Delta can only be calculated between views that share a root */);
        return this.forest.delta(view.forest);
    }
}
function getIndex(side, index) {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return (side + index);
}
//# sourceMappingURL=TreeView.js.map